import { Button, FormControl, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useUpdateShowFolder } from '~/api/show-folders';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { RenameFolderDialogProps__ShowFolder } from './ShowRenameFolderDialog.generated';

/* GraphQL */ `#graphql
fragment RenameFolderDialogProps__ShowFolder on ShowFolder {
  id
  name
}
`;

interface RenameFolderDialogProps {
  showFolder: RenameFolderDialogProps__ShowFolder;
  onClose: () => void;
  open: boolean;
}

export const ShowRenameFolderDialog = ({ showFolder, onClose, open }: RenameFolderDialogProps) => {
  const { t } = useTranslation();
  const [update] = useUpdateShowFolder();

  const formik = useFormik({
    initialValues: { name: showFolder.name },
    onSubmit: async (values) => {
      const { errors } = await update({
        variables: {
          input: {
            showFolderId: showFolder.id,
            patch: { name: values.name },
          },
        },
      });
      if (!errors?.length) onClose();
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: object({
      name: string().required().trim(),
    }),
  });

  const cancel = () => {
    onClose();
    formik.resetForm({ values: { name: showFolder.name } });
  };

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle onClose={cancel}>
          {t('pages.shows.wizards.dialogs.rename_folder.title')}
        </DialogTitle>

        <DialogContent>
          <FormControl fullWidth>
            <TextField
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              label={t('common.labels.name')}
              margin="dense"
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              variant="outlined"
              focused
              autoFocus
            />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={cancel}>
            {t('common.dialogs.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            {t('common.dialogs.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
